import React, { useEffect, useRef, useState } from 'react';
import { Col, Grid, Row, Divider } from 'rsuite';
import PageContent from '@/components/PageContent';
import { getCountriesLookup, getCountry } from '@/services/country.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { Circle, GoogleMap, InfoWindow, Marker, Polygon, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { getBoundingBoxCenter } from '@/utils/map';
import { hk_lat_lng } from '@/constants/map';
import { getActivityInfo, getPagedActivityInfos } from '@/services/activity-info.service';
import { defaultPaginationFilterRequest, LatLng, PaginatedSelectModel } from '@/models/core.model';
import { getHeight } from 'rsuite/esm/DOMHelper';
import { ActivityInfo, ActivityInfoPaginationFilterRequest, ActivityTypeList } from '@/models/activity-info.model';
import FetchingSelect from '@/components/FetchingSelect';
import { Route, RoutePaginationFilterRequest } from '@/models/route.model';
import { getPagedRoutes } from '@/services/route.service';
import { Country } from '@/models/country.model';
import { DiscoveryPoint, DiscoveryPointPaginationFilterRequest } from '@/models/discovery-point.model';
import { CityDistrict, CityDistrictPaginationFilterRequest } from '@/models/city-district.model';
import { getPagedDiscoveryPoints } from '@/services/discovery-point.service';
import { getPagedCityDistricts } from '@/services/city-district.service';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { jsDateToZone } from '@/utils/date-time';

const ActivityBugSolverPage = ({activityId: propActivityId}) => {
  const [activityId, setActivityId] = useState<number|undefined>();
  const [countryId, setCountryId] = useState<number|undefined>();
  const [cityId, setCityId] = useState<number|undefined>();
  const [mapCenter, setMapCenter] = useState<{ lat: number, lng: number }>();
  const [mapInfo, setMapInfo] = useState<{title: string, latLng: LatLng}|undefined>(undefined); 
  const [country, setCountry] = useState<Country|undefined>(undefined); 
  const [activity, setActivity] = useState<ActivityInfo|undefined>(undefined); 
  const [routes, setRoutes] = useState<Route[]>([]); 
  const [discoveryPoints, setDiscoveryPoints] = useState<DiscoveryPoint[]>([]);
  const [cityDistricts, setCityDistricts] = useState<CityDistrict[]>([]);
  const polylineRefs = useRef({});

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });

  useEffect(() => {
    setActivityId(propActivityId);
    handleActivityChange(propActivityId);
  }, []);  

  useEffect(() => {
    fetchMapData();
  }, [cityId, countryId]);  


  const getPolygonPathsFromGeoJson = function(geoJson: string) {
  try {
    if(!geoJson || geoJson.trim() === '') {
      return [];
    }
    // Parse the GeoJSON string
    const geometry = JSON.parse(geoJson);

    let polygonPaths;

    if (geometry.type === "Polygon") {
      // Single polygon
      polygonPaths = [geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }))];
    } else if (geometry.type === "MultiPolygon") {
      // Multiple polygons
      polygonPaths = geometry.coordinates.map( polygon =>
          polygon[0].map(([lng, lat]) => ({ lat, lng })) // only outer ring; skip holes
      );
    }

    return polygonPaths;
    
  } catch (error) {
    return [];
  }
  };

  const handleCountryChange = async (countryId: number | undefined) => {
    setCountryId(countryId);
    setCityId(undefined);

    if(countryId){
      //get country
      getCountry(countryId).then(result => {
        if(result.result){
          setCountry(result.result);
          if(result.result.jsonGeometry){
            const countryPolygon = getPolygonPathsFromGeoJson(result.result.jsonGeometry);
            if(countryPolygon && countryPolygon.length > 0){
              setMapCenter(getBoundingBoxCenter(countryPolygon[0]));
            }
          }
        }
        else{
          toastErrorMessage("Couldn't fetch country.");
        }
      });
    }
  };

  const handleCityChange = async (cityId: number | undefined) => {
    setCityId(cityId);
    setCountryId(undefined);
    setCountry(undefined);

    if(cityId){
      //get city
      getCity(cityId).then(result => {
        if(result.result){
          if(result.result.lat && result.result.lon){
            setMapCenter({lat: result.result.lat, lng: result.result.lon});
          }
        }
        else{
          toastErrorMessage("Couldn't fetch city.");
        }
      });
    }    
  };

  const fetchMapData = async () => {
    if(cityId || countryId){
      // get city districts
      const districtFilter: CityDistrictPaginationFilterRequest = {
        ...defaultPaginationFilterRequest,
        countryId: countryId,
        cityId: cityId,
        pageSize: 100
      };
      getPagedCityDistricts(districtFilter).then(result => {
        if(result.result){
          setCityDistricts(result.result);
        }
      });

      // get routes
      const routeFilter: RoutePaginationFilterRequest = {
        ...defaultPaginationFilterRequest,
        countryId: countryId,
        cityId: cityId,
        pageSize: 100
      };
      getPagedRoutes(routeFilter).then(result => {
        if(result.result){
          setRoutes(result.result);
        }
      });

      // get discovery points
      const discoveryPointFilter: DiscoveryPointPaginationFilterRequest = {
        ...defaultPaginationFilterRequest,
        countryId: countryId,
        cityId: cityId,
        pageSize: 100
      };
      getPagedDiscoveryPoints(discoveryPointFilter).then(result => {
        if(result.result){
          setDiscoveryPoints(result.result);
        }
      });
    }else {
      clearMapData();
    }
  };

  const clearMapData = () => {
      setCountry(undefined);
      setRoutes([]);
      setDiscoveryPoints([]);
      setCityDistricts([]);
  };

  const handleActivityChange = async (activityId: number) => {
    setActivityId(activityId);
    if(activityId){
      const result = await getActivityInfo(activityId);
      if(result.result){
        if(result.result.route){
          setActivity(result.result);
          setMapCenter(getBoundingBoxCenter(result.result.route));
        }else{
          toastErrorMessage("Route not specified for Activity.");
        }
      }
      else{
        toastErrorMessage("Couldn't fetch activity.");
      }
    }else{
      setActivity(undefined);
    }
  };

  const getActivityInfos = async (input?: string, page?: number): Promise<PaginatedSelectModel> => {
    const model: ActivityInfoPaginationFilterRequest = {
      pageNumber: page ?? 1,
      pageSize: 100,
      keyword: input,
      isAscending: false,
      hasRoute: true,
    };
    return await getPagedActivityInfos(model).then(data => 
      ({ 
        data: data.result.map(a => ({ 
          value: a.id, 
          label: `${a.id}, Type: ${ActivityTypeList.findLast(t=> t.value == a.activityType)?.label ?? '-'}, User: ${a.user.firstName} ${a.user.lastName}, Start: ${jsDateToZone(a.startTime, a.city?.ianaTimeZoneId)},  City: ${a.city?.name ?? '-'}, Country: ${a.country?.name ?? '-'}` 
        })),
        isLastPage: data.pageNumber >= data.totalPages,
      }));
  };

  const handleMouseOver = (event, title, key: string | undefined = undefined) => {
    setMapInfo({ title, latLng: event.latLng });
    if(key){
      const ref = polylineRefs.current[key];
      ref.setOptions({
        strokeOpacity: 1, // Ensure full opacity for visibility
        strokeWeight: 10,
        zIndex: 1000 // Bring it to front
      });    
    }
  };

  const handleMouseOut = (event, key: string | undefined = undefined) => {
    setMapInfo(undefined);
    if(key){
      const ref = polylineRefs.current[key];
      ref.setOptions({
        strokeOpacity: 0.7,
        strokeWeight: 7,
      });
    }
  };
  const activityPolylineOptions = {
    strokeColor: '#FF6F61', // coral-red color
    strokeOpacity: 0.7,
    strokeWeight: 7,
    geodesic: true,
  }; 
  const routePolylineOptions = {
    strokeColor: '#808000	', 
    strokeOpacity: 0.7,
    strokeWeight: 7,
    geodesic: true,
  }; 
  
  const countryPolygonOptions = {
    fillColor: '#000080',
    fillOpacity: 0.1,
    strokeColor: '#000080',
    strokeOpacity: 1,
    strokeWeight: 4,
  }; 
  
  const cityDistrictPolygonOptions = {
    fillColor: '#2196f3',
    fillOpacity: 0.05,
    strokeColor: '#2196f3',
    strokeOpacity: 1,
    strokeWeight: 2,
  }; 
  
  const discoveryPointCircleOptions = {
    strokeColor: '#FFC300',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#FFC300',
    fillOpacity: 0.2
  };
  
  const mapContainerStyle = { 
    width: '100%', 
    height: `${getHeight(window) - 250}px` , 
    borderRadius: 10 
  };
  
  
  return (
    <>
      <PageContent>
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:6}} >
                <label>Country</label>
                <FetchingSelect block
                  value={countryId}
                  fetchData={(text, page) => getCountriesLookup(text, page)} 
                  onChange={handleCountryChange}
                />
              </Col>
              <Col span={{xs:24, xl:6}} >
                <label>City</label>
                <FetchingSelect block
                  value={cityId}
                  fetchData={(text, page) => getCitiesLookup(text, undefined, page)} 
                  onChange={handleCityChange}
                />
              </Col>
              <Col span={{xs:24, xl:12}} >
                <label>Activity</label>
                <FetchingSelect block
                  value={activityId}
                  fetchData={getActivityInfos}
                  onChange={handleActivityChange}
                />
              </Col>
            </Row>
            <Row>
              {isLoaded ? <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center = {mapCenter || hk_lat_lng}
                zoom={12}
              >
                {country && country.jsonGeometry && <Polygon
                  paths={getPolygonPathsFromGeoJson(country.jsonGeometry)}
                  options={countryPolygonOptions}
                />}

                {cityDistricts.map(district => district.jsonGeometry && <Polygon
                  key={`dis-${district.id}`}
                  paths={getPolygonPathsFromGeoJson(district.jsonGeometry)}
                  options={cityDistrictPolygonOptions}
                  onMouseOver={e => handleMouseOver(e,`City District: ${district.name}` )}
                  onMouseOut={handleMouseOut}                  
                />)}

                {activity && activity.route && activity.route.length > 1 && <Polyline
                  path={activity.route}
                  onLoad={(polylineInstance) => {
                    polylineRefs.current[`act-${activity.id}`] = polylineInstance;
                    polylineInstance.setOptions(activityPolylineOptions);
                  }}                     
                  onMouseOver={e => handleMouseOver(e, `Activity Id: ${activity.id} (${activity.user.firstName} ${activity.user.lastName})`, `act-${activity.id}`)}
                  onMouseOut={e => handleMouseOut(e, `act-${activity.id}`)}
                />}

                {routes.map(route => route.path && route.path.length > 1 && <Polyline
                  key={`r-${route.id}`}
                  path={route.path}
                  onLoad={(polylineInstance) => {
                    polylineRefs.current[`rt-${route.id}`] = polylineInstance;
                    polylineInstance.setOptions(routePolylineOptions);
                  }}                  
                  onMouseOver={e => handleMouseOver(e,`Route: ${route.name}`, `rt-${route.id}`)}
                  onMouseOut={e => handleMouseOut(e, `rt-${route.id}`)}
                />)}

                {discoveryPoints.map(point => 
                  <React.Fragment key={`dp-${point.id}`}>
                    <Marker
                      title={point.name}
                      key={`dp-${point.id}`} 
                      position={{lat: point.lat, lng: point.lon}} 
                    />
                    {point.radius && <Circle
                      center={{lat: point.lat, lng: point.lon}} 
                      radius={point.radius}
                      options={discoveryPointCircleOptions}
                    />}
                  </React.Fragment>
                )}                
                {/* Tooltip */}
                {mapInfo && (
                  <InfoWindow position={mapInfo.latLng}>
                    <strong>{mapInfo.title}</strong>
                  </InfoWindow>
                )}                
              </GoogleMap> : '' }                   
            </Row>
          </Grid>
          <Divider />
      </PageContent>
    </>
  );
};

export default ActivityBugSolverPage;
